import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { CloudUpload, TriangleAlert, Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { useNavigate, useParams } from "@tanstack/react-router";
import { SideSheet, Button } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import { UploadDropZone } from "./UploadDropZone";
import { UploadFileRow } from "./UploadFileRow";
import { UploadProcessingView } from "./UploadProcessingView";
import { useUploadDocuments } from "@/hooks/use-upload-documents";
import type { ConflictResolution } from "@/hooks/use-upload-documents";
import { FileIcon } from "../FileIcon";
import { DuplicateConflictModal } from "./DuplicateConflictModal";
import type { DocumentView } from "../types";
import { useUploadSessionStore } from "@/stores/upload-session-store";
import type { Id } from "@/packages/types";
import { AuditContextBadge } from "./AuditContextBadge";
import { AuditMultiSelect } from "./AuditMultiSelect";
import { BatchCategoryAssign } from "./BatchCategoryAssign";
import { useApiMutation } from "@/packages/features-shared";
import { api } from "convex/_generated/api";
import { useWorkspaceId } from "@/hooks";
import type { ResolvedSuggestion } from "@/hooks/use-checklist-suggestions";

interface UploadSheetProps {
  open: boolean;
  onClose: () => void;
  recentDocuments?: DocumentView[];
  /** When set, the upload sheet is in audit-view mode (read-only badge, scoped to this audit). */
  preSelectedAudit?: {
    id: Id<"audits">;
    displayName: string;
    code: string;
  };
}

export function UploadSheet({
  open,
  onClose,
  recentDocuments = [],
  preSelectedAudit,
}: UploadSheetProps) {
  const { t } = useTranslation(["documents", "common"]);
  const { t: tAudits } = useTranslation("audits");

  const workspaceId = useWorkspaceId();

  // Selection & suggestion state for batch-confirm integration
  const [selectedByDoc, setSelectedByDoc] = useState<
    Record<string, Set<Id<"checklistItems">>>
  >({});
  const [suggestionsByDoc, setSuggestionsByDoc] = useState<
    Record<string, ResolvedSuggestion[]>
  >({});
  const [confirmedByDoc, setConfirmedByDoc] = useState<
    Record<
      string,
      { total: number; accepted: number; items: ResolvedSuggestion[] }
    >
  >({});
  const [isAcceptingAll, setIsAcceptingAll] = useState(false);

  const sessionFiles = useUploadSessionStore((s) => s.files);

  const acceptMutation = useApiMutation(
    api.document_mutations.acceptChecklistSuggestions,
    {
      name: "accept-checklist-suggestions",
      successMessage: tAudits("suggestions.successAccept"),
    },
  );

  const handleSelectedChange = (
    documentId: Id<"documents">,
    ids: Set<Id<"checklistItems">>,
  ) => {
    setSelectedByDoc((prev) => ({ ...prev, [String(documentId)]: ids }));
  };

  const handleSuggestionsReady = (
    documentId: Id<"documents">,
    suggestions: ResolvedSuggestion[],
  ) => {
    setSuggestionsByDoc((prev) => ({
      ...prev,
      [String(documentId)]: suggestions,
    }));
  };

  const handleConfirmAll = async () => {
    setIsAcceptingAll(true);
    try {
      const promises = sessionFiles.map(async (file) => {
        if (!file.documentId) return;
        const docId = file.documentId;
        const sugs = suggestionsByDoc[String(docId)] ?? [];
        if (sugs.length === 0) return;
        const selectedSet =
          selectedByDoc[String(docId)] ??
          new Set(sugs.map((s) => s.checklistItemId));
        const accepted = Array.from(selectedSet);
        const rejected = sugs
          .map((s) => s.checklistItemId)
          .filter((id) => !selectedSet.has(id));
        await acceptMutation.mutateAsync({
          documentId: docId,
          acceptedItemIds: accepted,
          rejectedItemIds: rejected,
          workspaceId,
        });
        const confirmedData = {
          total: sugs.length,
          accepted: accepted.length,
          items: sugs.filter((s) => selectedSet.has(s.checklistItemId)),
        };
        setConfirmedByDoc((prev) => ({
          ...prev,
          [String(docId)]: confirmedData,
        }));
      });
      await Promise.all(promises);
    } finally {
      setIsAcceptingAll(false);
    }
  };
  const {
    files,
    phase,
    hasInteracted,
    maxFiles,
    canAddMore,
    addFiles,
    removeFile,
    renameFile,
    addTag,
    removeTag,
    setFileCategoryId,
    setFileExpirationDate,
    reset,
    startUpload,
    conflicts,
    resolveConflicts,
    nextAvailableNames,
  } = useUploadDocuments();

  const [showConflictModal, setShowConflictModal] = useState(false);
  const [resolvedConflictNames, setResolvedConflictNames] = useState<
    Set<string>
  >(new Set());
  const [fileValidationErrors, setFileValidationErrors] = useState<
    Map<string, string>
  >(new Map());
  const [validationAttempt, setValidationAttempt] = useState(0);
  const [auditHighlight, setAuditHighlight] = useState(false);
  const auditTriggerRef = useRef<HTMLButtonElement>(null);

  const navigate = useNavigate();
  const { workspaceSlug } = useParams({ strict: false });

  const store = useUploadSessionStore();

  // Sync store when opening in audit-view mode
  // biome-ignore lint/correctness/useExhaustiveDependencies: preSelectedAudit is recreated on each parent render, only the ID matters
  useEffect(() => {
    const auditId = preSelectedAudit?.id;
    if (open && auditId) {
      store.setSelectedAuditId(auditId);
    }
  }, [open, preSelectedAudit?.id]);

  const unresolvedConflicts = useMemo(
    () => conflicts.filter((c) => !resolvedConflictNames.has(c.fileName)),
    [conflicts, resolvedConflictNames],
  );

  // Auto-open conflict modal as soon as unresolved duplicates are detected
  useEffect(() => {
    if (unresolvedConflicts.length > 0 && !showConflictModal) {
      setShowConflictModal(true);
    }
  }, [unresolvedConflicts.length, showConflictModal]);

  const handleClose = () => {
    if (phase === "processing" || phase === "completed") {
      onClose();
      return;
    }
    setResolvedConflictNames(new Set());
    setFileValidationErrors(new Map());
    store.setSelectedAuditId(null);
    store.setExplicitNoAudit(false);
    reset();
    onClose();
  };

  const handleRenameFile = useCallback(
    (id: string, newName: string) => {
      const file = files.find((f) => f.id === id);
      if (file && resolvedConflictNames.has(file.originalName)) {
        setResolvedConflictNames((prev) => {
          const next = new Set(prev);
          next.delete(file.originalName);
          return next;
        });
      }
      setFileValidationErrors((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      renameFile(id, newName);
    },
    [files, resolvedConflictNames, renameFile],
  );

  const handleFilesSelected = (newFiles: File[]) => {
    const overflow = addFiles(newFiles);
    if (overflow > 0) {
      toast.warning(
        `${overflow} ${t("upload.errors.filesLimitExceeded")} ${maxFiles}`,
      );
    }
  };

  const handleUploadMore = () => {
    reset();
  };

  const handleRecentDocumentClick = useCallback(
    (docId: string) => {
      reset();
      onClose();
      navigate({
        to: "/$workspaceSlug/documents/$documentId",
        params: { workspaceSlug: workspaceSlug ?? "", documentId: docId },
      });
    },
    [reset, onClose, navigate, workspaceSlug],
  );

  const handleUpload = () => {
    // In documents-list mode, require explicit audit selection before proceeding
    if (!preSelectedAudit) {
      const hasAuditSelection =
        store.selectedAuditId !== null || store.explicitNoAudit;
      if (!hasAuditSelection) {
        setAuditHighlight(true);
        auditTriggerRef.current?.click();
        return;
      }
    }

    // Validate all files
    const errors = new Map<string, string>();
    for (const file of files) {
      if (!file.displayName.trim()) {
        errors.set(file.id, t("documents:upload.errors.emptyFilename"));
        continue;
      }
      const isConflicting =
        conflicts.some((c) => c.fileName === file.displayName) &&
        !resolvedConflictNames.has(file.displayName);
      if (isConflicting) {
        errors.set(file.id, t("documents:upload.errors.duplicateName"));
        continue;
      }
      if (!file.categoryId) {
        errors.set(file.id, "missing-category");
      }
    }
    if (errors.size > 0) {
      setFileValidationErrors(errors);
      setValidationAttempt((n) => n + 1);
      return;
    }
    setFileValidationErrors(new Map());
    startUpload();
  };

  const handleConflictsResolved = (
    resolutions: Map<string, ConflictResolution>,
  ) => {
    resolveConflicts(resolutions);
    setResolvedConflictNames((prev) => {
      const next = new Set(prev);
      for (const key of resolutions.keys()) next.add(key);
      return next;
    });
    setShowConflictModal(false);
  };

  const handleConflictCancel = () => {
    // Remove all files that have unresolved duplicate conflicts
    const conflictNames = new Set(unresolvedConflicts.map((c) => c.fileName));
    for (const file of files) {
      if (conflictNames.has(file.displayName)) {
        removeFile(file.id);
      }
    }
    setShowConflictModal(false);
  };

  const handleBatchCategoryAssign = (categoryId: Id<"documentCategories">) => {
    for (const file of files) {
      setFileCategoryId(file.id, categoryId);
    }
    // Clear category validation errors
    setFileValidationErrors((prev) => {
      const next = new Map(prev);
      for (const [id, err] of prev) {
        if (err === "missing-category") next.delete(id);
      }
      return next;
    });
  };

  const duplicateFileNames = useMemo(
    () => new Set(conflicts.map((c) => c.fileName)),
    [conflicts],
  );

  const hasFiles = files.length > 0;
  const isUploading = phase === "uploading";
  const isProcessing = phase === "processing";
  const isCompleted = phase === "completed";
  const showUploadForm = phase === "idle";
  const showRecentUploads = !hasInteracted && recentDocuments.length > 0;
  const showProcessingView = isProcessing || isCompleted;

  // Processing/completed view
  if (showProcessingView) {
    return (
      <SideSheet open={open} onClose={handleClose}>
        <SideSheet.Header
          title={t("documents:upload.title")}
          icon={<CloudUpload size={18} />}
        />
        <SideSheet.Body>
          <AnimatePresence mode="wait">
            <motion.div
              key="processing-view"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
            >
              <UploadProcessingView
                selectedMap={selectedByDoc}
                onSelectedChange={handleSelectedChange}
                suggestionsMap={suggestionsByDoc}
                onSuggestionsReady={handleSuggestionsReady}
                externalConfirmedMap={confirmedByDoc}
                showConfirmButton={false}
              />
            </motion.div>
          </AnimatePresence>
        </SideSheet.Body>
        <SideSheet.Footer>
          <Button variant="secondary" onClick={handleClose}>
            {t("common:actions.close")}
          </Button>
          <Button variant="secondary" onClick={handleUploadMore}>
            {t("documents:upload.uploadMore")}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmAll}
            disabled={
              isAcceptingAll || Object.keys(suggestionsByDoc).length === 0
            }
            loading={isAcceptingAll}
          >
            {isAcceptingAll ? (
              <Loader2 size={14} className="animate-spin mr-1.5" />
            ) : null}
            {t("documents:upload.suggestions.confirm", {
              count: Object.keys(suggestionsByDoc).reduce((sum, docId) => {
                const sugs = suggestionsByDoc[docId] ?? [];
                if (sugs.length === 0) return sum;
                const selected =
                  selectedByDoc[docId] ??
                  new Set(sugs.map((s) => s.checklistItemId));
                return sum + selected.size;
              }, 0),
            })}
          </Button>
        </SideSheet.Footer>
      </SideSheet>
    );
  }

  return (
    <>
      <SideSheet open={open} onClose={handleClose}>
        <SideSheet.Header
          title={t("documents:upload.title")}
          icon={<CloudUpload size={18} />}
        />
        <SideSheet.Body>
          {/* ── Audit context bar ── */}
          {preSelectedAudit ? (
            <AuditContextBadge audit={preSelectedAudit} />
          ) : (
            <AuditMultiSelect
              value={store.selectedAuditId}
              onChange={(id) => {
                store.setSelectedAuditId(id);
                setAuditHighlight(false);
              }}
              explicitNoAudit={store.explicitNoAudit}
              onNoAuditChange={(val) => {
                store.setExplicitNoAudit(val);
                setAuditHighlight(false);
              }}
              highlight={auditHighlight}
              triggerRef={auditTriggerRef}
            />
          )}

          <AnimatePresence mode="wait">
            {/* ── Empty state: full-bleed drop zone ── */}
            {!hasFiles && showUploadForm && (
              <motion.div
                key="dropzone-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex-1 flex flex-col"
              >
                <UploadDropZone
                  collapsed={false}
                  onFilesSelected={handleFilesSelected}
                  disabled={!canAddMore || isUploading}
                />

                {/* Recent uploads — visible only before any interaction */}
                {showRecentUploads && (
                  <div className="mt-auto pt-8">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-[11px] font-semibold text-muted/50 uppercase tracking-[0.06em]">
                        {t("documents:upload.recentUploads")}
                      </h3>
                    </div>
                    <div className="space-y-0.5">
                      {recentDocuments.slice(0, 3).map((doc) => (
                        <button
                          key={`recent-${doc.id}`}
                          type="button"
                          onClick={() => handleRecentDocumentClick(doc.id)}
                          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 hover:bg-page/60 transition-colors cursor-pointer text-left group"
                        >
                          <FileIcon type={doc.type} />
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] text-primary truncate group-hover:text-accent transition-colors">
                              {doc.title}
                            </p>
                            <p className="text-[11px] text-muted/50">
                              {doc.size}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* ── Files selected: document list ── */}
            {hasFiles && (
              <motion.div
                key="file-list"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {/* Validation banner */}
                <AnimatePresence>
                  {fileValidationErrors.size > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="mb-4"
                    >
                      <div className="flex items-center gap-2 rounded-lg bg-red-500/[0.05] px-3 py-2">
                        <TriangleAlert
                          size={12}
                          className="shrink-0 text-red-500"
                        />
                        <span className="text-[12px] font-medium text-red-500">
                          {t("documents:upload.errors.validationBanner", {
                            count: fileValidationErrors.size,
                          })}
                        </span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* File count + batch category */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] font-semibold text-secondary uppercase tracking-[0.06em]">
                    {t("documents:upload.selectedFiles")}
                    <span className="ml-1.5 text-muted/70 normal-case tabular-nums font-medium">
                      {files.length}/{maxFiles}
                    </span>
                  </span>

                  {files.length > 1 && (
                    <BatchCategoryAssign onAssign={handleBatchCategoryAssign} />
                  )}
                </div>

                {/* File entries */}
                <div className="space-y-2">
                  <AnimatePresence mode="popLayout">
                    {files.map((file, index) => (
                      <motion.div
                        key={file.id}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <UploadFileRow
                          file={file}
                          onRename={handleRenameFile}
                          onRemove={(id) => {
                            const removedFile = files.find((f) => f.id === id);
                            if (removedFile) {
                              setResolvedConflictNames((prev) => {
                                const next = new Set(prev);
                                next.delete(removedFile.originalName);
                                return next;
                              });
                            }
                            setFileValidationErrors((prev) => {
                              if (!prev.has(id)) return prev;
                              const next = new Map(prev);
                              next.delete(id);
                              return next;
                            });
                            removeFile(id);
                          }}
                          onAddTag={addTag}
                          onRemoveTag={removeTag}
                          onSetCategoryId={(fileId, categoryId) => {
                            setFileCategoryId(fileId, categoryId);
                            setFileValidationErrors((prev) => {
                              if (!prev.has(fileId)) return prev;
                              const next = new Map(prev);
                              next.delete(fileId);
                              return next;
                            });
                          }}
                          onSetExpirationDate={setFileExpirationDate}
                          disabled={isUploading}
                          isDuplicate={
                            !resolvedConflictNames.has(file.originalName) &&
                            duplicateFileNames.has(file.originalName)
                          }
                          validationError={fileValidationErrors.get(file.id)}
                          shakeKey={validationAttempt}
                        />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>

                {/* Add more files — collapsed link */}
                {showUploadForm && canAddMore && (
                  <div className="mt-3">
                    <UploadDropZone
                      collapsed={true}
                      onFilesSelected={handleFilesSelected}
                      disabled={!canAddMore || isUploading}
                    />
                  </div>
                )}

                {/* Uploading state */}
                {isUploading && (
                  <div className="mt-3">
                    <UploadDropZone
                      collapsed={true}
                      onFilesSelected={handleFilesSelected}
                      disabled={true}
                    />
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </SideSheet.Body>

        <SideSheet.Footer>
          <Button variant="secondary" onClick={handleClose}>
            {t("common:actions.cancel")}
          </Button>
          {hasFiles && (
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={isUploading}
              loading={isUploading}
            >
              {isUploading
                ? t("documents:upload.uploading")
                : `${t("documents:upload.uploadButton")} ${files.length > 1 ? `(${files.length})` : ""}`}
            </Button>
          )}
        </SideSheet.Footer>
      </SideSheet>

      {/* Duplicate conflict resolution modal */}
      <DuplicateConflictModal
        open={showConflictModal}
        conflicts={unresolvedConflicts}
        nextAvailableNames={nextAvailableNames}
        onResolveAll={handleConflictsResolved}
        onCancel={handleConflictCancel}
      />
    </>
  );
}
